import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import {
  getPurchasedGoodsFoodListQueryKey,
  getPurchasedGoodsOtherListQueryKey,
  getPurchasedGoodsPackagingListQueryKey,
  getPurchasedGoodsReviewListQueryKey,
  getPurchasedGoodsReviewStatsRetrieveQueryKey,
  usePurchasedGoodsBulkPartialUpdate,
} from '@shared/api/lib/purchased-goods/purchased-goods';
import { useQueryClient } from '@tanstack/react-query';

type BulkMutationOptions = NonNullable<
  Parameters<typeof usePurchasedGoodsBulkPartialUpdate>[0]
>['mutation'];

/**
 * Unified bulk action on purchased goods. The action travels inside the
 * payload — `confirm` / `model-as-basic-food` / `model-as-recipe` on food
 * activities, or `change-type` (with `targetType`) on uncategorised goods — and
 * each action carries only its own fields.
 *
 * Mutation options are passed through per call site rather than baked in:
 * change-type wants list invalidation (see `useInvalidatePurchasedGoodsLists`),
 * while the food-review actions deliberately rely on optimistic resolution
 * instead of an immediate refetch that could re-surface not-yet-processed rows.
 */
export const useBulkUpdatePurchasedGoods = (mutation?: BulkMutationOptions) =>
  usePurchasedGoodsBulkPartialUpdate({
    request: useAxiosConfig(),
    mutation,
  });

/**
 * Invalidate every list a change-type action can move an item between
 * (Excluded items, Food & drink, Packaging) so each surface reflects server
 * truth immediately — the enqueue flips item_type synchronously, so the row
 * leaves its source list on the next refetch.
 */
export const useInvalidatePurchasedGoodsLists = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries(getPurchasedGoodsReviewListQueryKey()),
      queryClient.invalidateQueries(getPurchasedGoodsOtherListQueryKey()),
      queryClient.invalidateQueries(
        getPurchasedGoodsReviewStatsRetrieveQueryKey()
      ),
      queryClient.invalidateQueries(getPurchasedGoodsFoodListQueryKey()),
      queryClient.invalidateQueries(getPurchasedGoodsPackagingListQueryKey()),
    ]);
};
